<template>
  <div ref="eventsEl" :key="componentKey" class="events" :class="{ isEmpty }">
    <!-- FIXME TRANSITIONS: This should be suspensed in the parents component
    but the back-end has to change the return to stop returning null while
    waiting. Basically to just have a normal Promise. -->
    <Loader v-if="events === null">
      <BaseTimelineSkeleton />
    </Loader>

    <div v-else-if="events" class="events-container">
      <div class="all-day" :class="{ isEmpty: !allDayEventsIds?.length }">
        <div class="header"></div>
        <div v-if="allDayEventsIds?.length" class="events">
          <EventAllDayWrapper
            v-for="eventId in allDayEventsIds"
            :key="eventId"
            :event-id="eventId"
            :is-selected="selectedEventId === eventId"
            :is-single-event="allDayEventsIds.length === 1"
            @select="selectEvent"
            @unselect="unselectEvent"
          />
        </div>
      </div>

      <Drop
        id="calendar-drop-zone"
        ref="eventsListEl"
        tag="ul"
        class="eventslist"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
      >
        <div v-if="showTimeIndicators" class="time-indicators">
          <div class="time-indicator start">{{ formattedStartHour }}</div>
        </div>
        <div v-if="!isEmpty || isEmptyBreakOvered" class="bar" />
        <li
          v-for="(event, i) in displayEvents"
          :key="getEventOrPauseId(event, i)"
        >
          <BreakBlock
            v-if="event.type === 'pause'"
            :selected-calendar-id="selectedCalendarId"
            :calendars-map="calendarsMap"
            :key="`break-${getEventOrPauseId(event, i)}-${componentKey}`"
            :date="date"
            :event="event"
            :is-empty="isEmpty"
            :reset-drag-signal="resetDragSignal"
            @is-overing="onOveringEmptyBreak"
            @scroll-top="(v) => (scrollingTop = v)"
            @scroll-bottom="(v) => (scrollingBottom = v)"
            @set-current-event-el="setCurrentEventEl"
            @dragover="onDragOver"
            @dragleave="onDragLeave"
            @drop="onDrop"
            @update-event="updateEvent"
          />
          <EventBlock
            v-else
            :key="`event-${getEventOrPauseId(event, i)}-${componentKey}`"
            :boundaries="boundaries"
            :date="date"
            :event="event"
            :is-recording="recordingEventId === event.id"
            :dragging-over="
              isEventDragging || isTaskDragging || isAllocationDragging
            "
            :event-before="displayEvents?.[i - 1]"
            :event-after="displayEvents?.[i + 1]"
            :selected-event-id="selectedEventId"
            :events-el="eventsEl"
            @select="selectEvent"
            @unselect="unselectEvent"
            @set-current-event-el="setCurrentEventEl"
            @update="refresh"
          />
        </li>
        <div v-if="showTimeIndicators" class="time-indicator-end">
          {{ formattedEndHour }}
        </div>
      </Drop>
    </div>
  </div>
</template>

<script lang="ts">
import { isSameDay } from "date-fns";
import {
  ComponentPublicInstance,
  computed,
  defineComponent,
  nextTick,
  ref,
  toRefs,
  watch,
} from "vue";
import { Drop } from "vue-easy-dnd";

import { Daytime, EventOrPause, EventPatch } from "@ctrl/controller-rpc";

import assert from "@/assert";
import {
  isDraggingAllocation,
  isDraggingEvent,
  isDraggingTask,
  useDndEvent,
} from "@/components/agenda/dnd";
import BaseTimelineSkeleton from "@/components/base/BaseTimelineSkeleton.vue";
import { isMidnightEndDaytime } from "@/components/dates";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import { safeUpdateEvent } from "@/components/modal";
import { UseSettings } from "@/components/settings/controller/settings";
import { getHTMLElement } from "@/components/utils";
import { useAudioRecording } from "@/composables/recording/useAudioRecording";
import { bindAsync, useReleaser } from "@/ref";
import { DraggedBase } from "@/types";

import BreakBlock from "./BreakBlock.vue";
import EventAllDayWrapper from "./EventAllDayWrapper.vue";
import EventBlock from "./EventBlock.vue";

export type Boundaries = {
  start: Daytime;
  end: Daytime;
};

export default defineComponent({
  components: {
    BaseTimelineSkeleton,
    Loader,
    BreakBlock,
    EventAllDayWrapper,
    Drop,
    EventBlock,
  },
  props: {
    date: { type: Date, required: true },
    selectedCalendarId: { type: String },
    calendarsMap: { type: Object, required: true },
  },
  emits: {
    unselectItems: () => true,
  },
  async setup(props, { emit }) {
    const { date } = toRefs(props);
    const controller = inject.controller();
    const calendars = inject.calendars();
    const locale = inject.locale();
    const { is24hFormat } = UseSettings.getInstance();
    const baseLogger = inject.logger();
    const releaser = useReleaser();
    const audioRecording = releaser.add(useAudioRecording(baseLogger));
    const simpleModal = inject.simpleModal();
    const multipleEventsModal = inject.multipleEventsModal();
    const componentKey = ref(0);
    const eventsEl = ref<HTMLElement>();
    const eventsListEl = ref<InstanceType<typeof Drop>>();
    const currentEventEl = ref<HTMLElement>();
    releaser.defer(() => {
      eventsEl.value = undefined;
      eventsListEl.value = undefined;
      currentEventEl.value = undefined;
    });
    const scrollTopAnimation = ref<number | null>(null);
    const scrollBottomAnimation = ref<number | null>(null);
    releaser.defer(() => {
      if (scrollTopAnimation.value)
        window.cancelAnimationFrame(scrollTopAnimation.value);
      if (scrollBottomAnimation.value)
        window.cancelAnimationFrame(scrollBottomAnimation.value);
    });

    // Track Alt/Option to detect allocation duplication mode.
    // In duplication mode (Alt + allocation drag), we should NOT hide the dragged allocation.
    const altPressed = ref(false);
    const onAltKeyDown = (ev: KeyboardEvent) => {
      altPressed.value = !!ev.altKey;
    };
    const onAltKeyUp = (ev: KeyboardEvent) => {
      altPressed.value = !!ev.altKey;
    };
    window.addEventListener("keydown", onAltKeyDown, {
      capture: true,
      passive: true,
    });
    window.addEventListener("keyup", onAltKeyUp, {
      capture: true,
      passive: true,
    });
    releaser.defer(() => {
      window.removeEventListener("keydown", onAltKeyDown, true);
      window.removeEventListener("keyup", onAltKeyUp, true);
    });

    const rawEventsPromise = releaser.add(
      bindAsync(date, (date) =>
        Promise.resolve(controller.event.timeline(date))
      )
    );
    const baseAllDayEventsIdsPromise = releaser.add(
      bindAsync(date, (date) =>
        Promise.resolve(controller.event.day(date, "only-all-day", true))
      )
    );
    const hoursConfPromise = releaser.add(controller.settings.hours());
    const [unfilteredEvents, baseAllDayEventsIds, hoursConf] =
      await Promise.all([
        rawEventsPromise,
        baseAllDayEventsIdsPromise,
        hoursConfPromise,
      ]);
    // Drop events of disabled calendars.
    const rawEvents = computed(() => {
      const events = unfilteredEvents.value;
      if (!events) return null;
      return events.filter((event: EventOrPause) => {
        if (event.type === "pause") return true;
        const calendar = calendars.value?.byId.get(event.calendar);
        return calendar ? !calendar.disabled : true;
      });
    });

    // A 24:00 working-day end is encoded as { hours: 0 } in the hours
    // settings (see isMidnightEndDaytime), which the controller reads
    // literally as 00:00: it then omits the trailing evening pause after the
    // last event and returns an empty timeline for a day without events.
    // Normalize the boundary to 24:00 and compensate below.
    const dayEndDaytime = computed<Daytime>(() =>
      isMidnightEndDaytime(hoursConf.value.evening)
        ? { hours: 24, minutes: 0, seconds: 0 }
        : hoursConf.value.evening
    );
    const daytimeToDate = (daytime: Daytime): Date => {
      const d = new Date(date.value);
      d.setHours(daytime.hours, daytime.minutes ?? 0, daytime.seconds ?? 0, 0);
      return d;
    };
    // The controller returns an empty timeline for a day without any events
    // (instead of a lone pause). Synthesize a working-day pause so the empty
    // state and its task-drop zone still render.
    const emptyDayPause = (): EventOrPause => ({
      type: "pause",
      kind: "pause",
      start: daytimeToDate(hoursConf.value.day),
      end: daytimeToDate(dayEndDaytime.value),
    });

    // Filter out multi-day events for timeline display
    const events = computed(() => {
      const _events = rawEvents.value;
      if (!_events) {
        return null;
      }
      // Filter out events that span multiple days, but only if the current date
      // is NOT the first or last day of the event
      const filtered = _events.filter((_event) => {
        if (_event.type === "pause") {
          return true;
        }
        const isMultiDay = !isSameDay(_event.start, _event.end);
        if (!isMultiDay) {
          return true;
        }
        // Keep in timeline if current date is first or last day
        const isFirstDay = isSameDay(date.value, _event.start);
        const isLastDay = isSameDay(date.value, _event.end);
        return isFirstDay || isLastDay;
      });
      if (filtered.length === 0) return [emptyDayPause()];
      // With a midnight-encoded evening, append the trailing evening pause
      // the controller omitted, so the day accepts drops until midnight.
      if (isMidnightEndDaytime(hoursConf.value.evening)) {
        const dayEnd = daytimeToDate(dayEndDaytime.value);
        const lastEnd = new Date(
          Math.max(...filtered.map((item) => item.end.getTime()))
        );
        if (lastEnd < dayEnd) {
          const last = filtered[filtered.length - 1];
          if (last.type === "pause") {
            filtered[filtered.length - 1] = { ...last, end: dayEnd };
          } else {
            filtered.push({
              type: "pause",
              kind: "evening",
              start: lastEnd,
              end: dayEnd,
            });
          }
        }
      }
      return filtered;
    });

    // Combine all-day events with multi-day events
    const allDayEventsIds = computed(() => {
      const baseIds = baseAllDayEventsIds.value || [];
      const _events = rawEvents.value;
      if (!_events) {
        return baseIds;
      }
      // Collect IDs of multi-day events where the current date is in the middle
      // (not the first or last day)
      const multiDayEventIds = _events
        .filter(
          (_event): _event is EventOrPause & { type: "event"; id: string } => {
            if (_event.type !== "event") {
              return false;
            }
            const isMultiDay = !isSameDay(_event.start, _event.end);
            if (!isMultiDay) {
              return false;
            }
            // Only include if current date is NOT the first or last day
            const isFirstDay = isSameDay(date.value, _event.start);
            const isLastDay = isSameDay(date.value, _event.end);
            return !isFirstDay && !isLastDay;
          }
        )
        .map((_event) => _event.id);
      // Combine and deduplicate
      return [...new Set([...baseIds, ...multiDayEventIds])];
    });

    const getEventOrPauseId = (event: EventOrPause, i: number): string => {
      assert(events.value);
      if (event.type === "event") return event.id;
      else if (event.kind !== "pause") return `pause-${event.kind}`;
      else {
        const previous = events.value[i - 1];
        if (previous) return `pause-${getEventOrPauseId(previous, i - 1)}`;
        else return `pause-0`;
      }
    };
    const isEmpty = computed<boolean>(() => {
      return (
        !!events.value &&
        (events.value.length === 0 ||
          (events.value.length === 1 && events.value[0].type === "pause"))
      );
    });
    //Scroll
    const scrollingTop = ref(false);
    const scrollingBottom = ref(false);

    const scrollUp = () => {
      if (eventsListEl.value?.$el && scrollingTop.value) {
        eventsListEl.value.$el.scrollTo(
          0,
          eventsListEl.value.$el.scrollTop - 3
        );
        scrollTopAnimation.value = window.requestAnimationFrame(scrollUp);
      }
    };

    const scrollDown = () => {
      if (eventsListEl.value?.$el && scrollingBottom.value) {
        eventsListEl.value.$el.scrollTo(
          0,
          eventsListEl.value.$el.scrollTop + 3
        );
        scrollBottomAnimation.value = window.requestAnimationFrame(scrollDown);
      }
    };

    releaser.defer(
      watch(scrollingTop, (value) => {
        if (scrollTopAnimation.value) {
          window.cancelAnimationFrame(scrollTopAnimation.value);
          scrollTopAnimation.value = null;
        }
        if (value) {
          scrollTopAnimation.value = window.requestAnimationFrame(scrollUp);
        }
      })
    );

    releaser.defer(
      watch(scrollingBottom, (value) => {
        if (scrollBottomAnimation.value) {
          window.cancelAnimationFrame(scrollBottomAnimation.value);
          scrollBottomAnimation.value = null;
        }
        if (value) {
          scrollBottomAnimation.value =
            window.requestAnimationFrame(scrollDown);
        }
      })
    );
    //DND
    const draggingPlaceholderStyle = ref<{ height?: string }>({});
    const isEventDragging = computed(isDraggingEvent);
    const isTaskDragging = computed(isDraggingTask);
    const isAllocationDragging = computed(isDraggingAllocation);
    const isUpdateInProgress = ref(false);

    // Get the ID of the event currently being dragged (if any)
    const dndEvent = computed(useDndEvent);
    const draggedEventId = computed(() => {
      const data = dndEvent.value;
      if (!data) return null;
      return data.event?.id ?? null;
    });
    const isDuplicatingAllocation = computed(
      () => isAllocationDragging.value && altPressed.value
    );

    // When dragging an event, coalesce it with adjacent pauses into one large pause
    // This creates a unified drop zone to move the dragged event withint its time range
    const displayEvents = computed(() => {
      if (
        !events.value ||
        !draggedEventId.value ||
        isDuplicatingAllocation.value
      ) {
        return events.value;
      }

      const idx = events.value.findIndex(
        (e) => e.type === "event" && e.id === draggedEventId.value
      );
      if (idx === -1) return events.value;

      const before = events.value[idx - 1];
      const dragged = events.value[idx];
      const after = events.value[idx + 1];

      // Determine merged pause boundaries
      const mergedStart =
        before?.type === "pause" ? before.start : dragged.start;
      const mergedEnd = after?.type === "pause" ? after.end : dragged.end;

      // Build new array, replacing dragged event (and adjacent pauses) with one pause
      const result: EventOrPause[] = [];
      for (let i = 0; i < events.value.length; i++) {
        // Skip the pause before if it will be merged
        if (i === idx - 1 && before?.type === "pause") continue;
        // Replace the dragged event with a merged pause
        if (i === idx) {
          result.push({
            type: "pause",
            kind: "pause",
            start: mergedStart,
            end: mergedEnd,
          });
          continue;
        }
        // Skip the pause after if it will be merged
        if (i === idx + 1 && after?.type === "pause") continue;
        result.push(events.value[i]);
      }
      return result;
    });

    const onDragOver = (e: DraggedBase) => {
      const windowRect = document.body.getBoundingClientRect();
      // Increase scroll zones to reduce sensitivity and jerky behavior
      scrollingTop.value = e.position.y < windowRect.top + 150;
      scrollingBottom.value = e.position.y > windowRect.bottom - 150;
    };
    const onDragLeave = () => {
      resetDrag();
    };
    const onDrop = () => {
      // NOTE: do not refresh when dropping because we want to stay on the dropped item.
    };

    // Signal for child components to clear their drag state
    //
    // We use a ref instead of emit() here because the template has multiple
    // BreakBlock components; it is easier/cleaner than doing a one-by-one emit().
    const resetDragSignal = ref(0);

    const resetDrag = () => {
      draggingPlaceholderStyle.value = {};
      scrollingTop.value = false;
      scrollingBottom.value = false;
      isEmptyBreakOvered.value = false;

      // Explicitly clear scroll animations to ensure smooth scrolling stops
      if (scrollTopAnimation.value) {
        window.cancelAnimationFrame(scrollTopAnimation.value);
        scrollTopAnimation.value = null;
      }
      if (scrollBottomAnimation.value) {
        window.cancelAnimationFrame(scrollBottomAnimation.value);
        scrollBottomAnimation.value = null;
      }

      // Signal child components to reset their drag state
      resetDragSignal.value++;
    };

    releaser.defer(
      watch(
        [isEventDragging, isTaskDragging, isAllocationDragging],
        ([eventDnd, taskDnd, allocationDnd]) => {
          if ((eventDnd || taskDnd || allocationDnd) && eventsListEl.value) {
            draggingPlaceholderStyle.value = {
              height: `${eventsListEl.value.$el.clientHeight - 124}px`,
            };
          } else {
            resetDrag();
          }
        }
      )
    );

    //Current event
    const setCurrentEventEl = (el: HTMLElement | ComponentPublicInstance) => {
      if (el === currentEventEl.value) return;
      if (el instanceof HTMLElement) currentEventEl.value = el;
      else if (el.$el) currentEventEl.value = el.$el;
    };
    const eventsListHTMLElement = () => {
      const component = eventsListEl.value;
      return component ? getHTMLElement(component) : undefined;
    };
    const scrollToEnd = () => {
      setTimeout(() => {
        const el = eventsListHTMLElement();
        if (el && !isUpdateInProgress.value) {
          el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        }
      }, 100);
    };
    const scrollToFirstEvent = () => {
      setTimeout(() => {
        const container = eventsListHTMLElement();
        if (container && !isUpdateInProgress.value) {
          const firstEvent = container.querySelector(".event-block");
          if (firstEvent) {
            const containerRect = container.getBoundingClientRect();
            const eventRect = firstEvent.getBoundingClientRect();
            const scrollTop =
              container.scrollTop + (eventRect.top - containerRect.top) - 10;
            container.scrollTo({ top: scrollTop, behavior: "smooth" });
          }
        }
      }, 100);
    };
    const scrollToCurrent = () => {
      //This is required to have the scroll work everytime.
      setTimeout(() => {
        if (isUpdateInProgress.value) return;
        const today = new Date();
        const isToday = isSameDay(date.value, today);
        const container = eventsListHTMLElement();
        if (!container) return;

        // When the current "block" is a BreakBlock, the current time marker is
        // *inside* the block (Timeline renders `.time.current`), so scrolling to
        // the block top doesn't align "now" nicely. Prefer scrolling to that marker.
        const preferredTarget: Element | null =
          currentEventEl.value?.querySelector(".timeline .time.current") ??
          (isToday
            ? container.querySelector(".timeline .time.current")
            : null) ??
          currentEventEl.value ??
          null;

        if (preferredTarget && isToday) {
          const containerRect = container.getBoundingClientRect();
          const targetRect = preferredTarget.getBoundingClientRect();
          const topOffset = 56;
          const scrollTop =
            container.scrollTop +
            (targetRect.top - containerRect.top) -
            topOffset;
          container.scrollTo({ top: scrollTop, behavior: "smooth" });
          return;
        }

        // If there we couldn't find an item to scroll to, fallback to easier techniques:
        //   - today -> end of the day
        //   - otherwise -> first event
        if (isToday) {
          scrollToEnd();
        } else {
          scrollToFirstEvent();
        }
      }, 100);
    };
    releaser.defer(
      watch(
        currentEventEl,
        (value, oldValue) => {
          if (value && !oldValue) scrollToCurrent();
        },
        { immediate: true }
      )
    );
    releaser.defer(
      watch(
        () => date.value && date.value.getTime(),
        (_ts) => {
          scrollToCurrent();
        },
        { immediate: true }
      )
    );

    const isEmptyBreakOvered = ref(false);
    const onOveringEmptyBreak = (isOvering: boolean) =>
      (isEmptyBreakOvered.value = isOvering);
    const selectedEventId = ref<string>();
    const selectEvent = (id: string) => {
      emit("unselectItems");
      nextTick(() => {
        selectedEventId.value = id;
      });
    };
    const unselectEvent = () => {
      selectedEventId.value = undefined;
    };

    const boundaries = computed(() => {
      if (!events.value || events.value.length === 0) {
        return { start: hoursConf.value.day, end: dayEndDaytime.value };
      }

      const firstEventStart = events.value[0].start;
      const lastEventEnd = events.value[events.value.length - 1].end;

      const firstEventDaytime = {
        hours: firstEventStart.getHours(),
        minutes: firstEventStart.getMinutes(),
      };
      // An end rolling over to the next day means the timeline reaches
      // midnight: treat it as 24:00, not as 00:00 of the current day.
      const lastEventDaytime = isSameDay(lastEventEnd, date.value)
        ? {
            hours: lastEventEnd.getHours(),
            minutes: lastEventEnd.getMinutes(),
          }
        : { hours: 24, minutes: 0 };

      return {
        start:
          firstEventDaytime.hours < hoursConf.value.day.hours ||
          (firstEventDaytime.hours === hoursConf.value.day.hours &&
            firstEventDaytime.minutes < (hoursConf.value.day.minutes ?? 0))
            ? firstEventDaytime
            : hoursConf.value.day,
        end:
          lastEventDaytime.hours > dayEndDaytime.value.hours ||
          (lastEventDaytime.hours === dayEndDaytime.value.hours &&
            lastEventDaytime.minutes > (dayEndDaytime.value.minutes ?? 0))
            ? lastEventDaytime
            : dayEndDaytime.value,
      };
    });
    // formatDaytime rejects { hours: 24 } (our normalized midnight end), so
    // format that boundary explicitly, like the working hours settings do.
    const formatBoundaryHour = (daytime: Daytime) =>
      daytime.hours >= 24
        ? is24hFormat.value
          ? "24:00"
          : "12:00 AM"
        : locale.value.formatDaytime(daytime);
    const formattedStartHour = computed(() => {
      return formatBoundaryHour(boundaries.value.start);
    });
    const formattedEndHour = computed(() => {
      return formatBoundaryHour(boundaries.value.end);
    });

    // Track task dragging state with a computed to handle both global and break block dragging
    const showTimeIndicators = computed(
      () =>
        isEventDragging.value ||
        isTaskDragging.value ||
        isAllocationDragging.value
    );
    const recordingEventId = computed(() => {
      return audioRecording.status.value === "recording"
        ? audioRecording.ownerId.value
        : null;
    });
    const refresh = () => {
      // Preserve scroll position when dropping tasks to avoid jumping back to current time
      isUpdateInProgress.value = true;
      const currentScrollTop = eventsListEl.value?.$el?.scrollTop || 0;
      // Use a delay to ensure the DOM has updated after the drop, then restore scroll position
      setTimeout(() => {
        if (eventsListEl.value?.$el && currentScrollTop > 0) {
          eventsListEl.value.$el.scrollTop = currentScrollTop;
        }
        // Reset the flag after a short delay
        setTimeout(() => {
          isUpdateInProgress.value = false;
        }, 200);
      }, 100);
    };

    const updateEvent = (patch: EventPatch) => {
      safeUpdateEvent(
        "update",
        patch.id,
        { controller, simpleModal, multipleEventsModal },
        { patch }
      );
      refresh();
    };

    return {
      componentKey,
      displayEvents,
      isEventDragging,
      isTaskDragging,
      isAllocationDragging,
      isUpdateInProgress,
      draggingPlaceholderStyle,
      getEventOrPauseId,
      events,
      eventsEl,
      eventsListEl,
      isEmpty,
      isEmptyBreakOvered,
      onDragLeave,
      onDragOver,
      onDrop,
      onOveringEmptyBreak,
      scrollingBottom,
      scrollingTop,
      selectEvent,
      selectedEventId,
      setCurrentEventEl,
      unselectEvent,
      allDayEventsIds,
      formattedStartHour,
      formattedEndHour,
      showTimeIndicators,
      recordingEventId,
      updateEvent,
      resetDragSignal,
      refresh,
      boundaries,
    };
  },
});
</script>

<style scoped lang="scss">
.events {
  height: 100%;
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding-inline-start: 32px;
  padding-inline-end: 32px;

  &.isEmpty {
    max-width: none;
    width: 100%;
  }
}

.events-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
}

.all-day {
  position: relative;
  display: flex;
  padding-block: 12px;
  padding-inline-start: 0;
  column-gap: 27px;
  max-width: $agenda-max-width;
  @include themed() {
    background: t($bg);
  }

  &.isEmpty {
    display: none;
  }

  .events {
    width: 100%;
    height: auto;
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: row;
    gap: 6px;
    flex-wrap: wrap;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
}

ul.eventslist {
  display: flex;
  flex-direction: column;
  list-style-type: none;
  position: relative;
  padding: 20px 0 124px 0;
  margin: 0;
  height: max-content;
  width: 100%;
  max-width: $agenda-max-width;
  scrollbar-width: none;
  overflow-y: auto;
  flex: 1;
  mask-image: linear-gradient(to bottom, transparent, black 20px);
  -webkit-mask-image: linear-gradient(to bottom, transparent, black 20px);

  > li {
    position: relative;
    display: flex;
    scroll-margin-top: 150px;
    max-width: 100%;
  }

  .dragging-placeholder {
    position: absolute;
    width: 1px;
    z-index: $z-index-content;
    inset-inline-start: 75px;
    @include themed() {
      background: t($grey-15);
    }
  }
  .bar {
    // position: absolute;
    // inset-inline-start: 48.5px;
    // width: 0.5px;
    // height: calc(100% - 124px);
    // @include themed() {
    //   background: t($grey-15);
    // }
  }
}

.header {
  @extend %font-h5;
  line-height: 17px;
  inset-inline-start: 14px;
  width: 30px;
  text-align: center;
  margin-bottom: 12px;
  @include themed() {
    background: t($bg);
    color: t($grey-40);
  }
}

.time-indicators {
  position: absolute;
  inset-inline-start: 0;
  // Match the Timeline column width (56px) and center the indicators on it,
  // so they sit on the vertical line like the end indicator does.
  width: 56px;
  height: 100%;
  pointer-events: none;
  z-index: $z-index-content-elevated;
  display: flex;
  flex-direction: column;
  align-items: center;

  .time-indicator {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    @include themed() {
      background: t($bg);
      color: t($grey-50);
      border: 1px solid t($grey-15);
    }

    &.start {
      margin-top: -8px;
    }
  }
}

.time-indicator-end {
  display: inline-block;
  width: fit-content;
  max-width: fit-content;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  @include themed() {
    background: t($bg);
    color: t($grey-50);
    border: 1px solid t($grey-15);
  }
}
</style>
